import React, { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import {
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Paper,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { selectgpsData, selectgpsDatasStatus, nearestGpsData } from '../store/dashboard.selectors';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import question from './../../../assets/icons/question_icon.svg';

const MapComponent = (props) => {
  const {
    markers,
    handleClickMarker,
    setSnackbar,
    selectedMarker,
    showFullPath,
    setShowFullPath,
    onMarkerPositionChange,
    setShowZoomToSelected,
    zoomToSelected,
    setZoomToSelected,
    isAddSign,
    setAddSignLocation,
    allowEditMode,
    showSigns,
  } = props;

  const [mapLoaded, setMapLoaded] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [stackedEvents, setStackedEvents] = useState([]);
  const [currentStackKey, setCurrentStackKey] = useState('');
  const [groupedMarkers, setGroupedMarkers] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [markerElements, setMarkerElements] = useState({});
  const [selectedForDrag, setSelectedForDrag] = useState(null);

  const gpsData = useSelector(selectgpsData);
  const gpsDataLoading = useSelector(selectgpsDatasStatus);
  const nearestGps = useSelector(nearestGpsData);

  const mapRef = useRef(null);
  const anchorRef = useRef(null);

  const roleid = JSON.parse(localStorage.getItem('user')).roleid;
  const isAdmin = roleid === 1 || roleid === 2;

  // determine whether the edit mode switch should be rendered
  const showEditToggle = isAdmin && !isAddSign && allowEditMode;

  // Suppress ResizeObserver errors from map libraries
  useEffect(() => {
    const handleError = (event) => {
      if (event.message?.includes('ResizeObserver loop')) {
        event.stopImmediatePropagation();
      }
    };

    // Override console.error to catch ResizeObserver logs
    const originalError = console.error;
    console.error = (...args) => {
      const message = args?.[0]?.toString?.() || '';
      if (message.includes('ResizeObserver loop')) {
        return; // Silently ignore
      }
      originalError.apply(console, args);
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      console.error = originalError;
    };
  }, []);

  const groupMarkersByPosition = (markers) => {
    return markers.reduce((grouped, marker) => {
      if (marker && typeof marker.lat === 'number' && typeof marker.lng === 'number') {
        const key = `${marker.lat}_${marker.lng}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(marker);
      }
      return grouped;
    }, {});
  };

  const createStackedMarkerContent = (events, stackKey, isDraggable = false) => {
    const container = document.createElement('div');
    container.style.position = 'relative';
    container.style.cursor = isDraggable ? 'pointer' : 'pointer';

    events.forEach((event, index) => {
      const markerDiv = document.createElement('div');
      markerDiv.style.position = 'absolute';
      markerDiv.style.width = '30px';
      markerDiv.style.height = '30px';
      markerDiv.style.borderRadius = '50%';
      markerDiv.style.overflow = 'hidden';
      markerDiv.style.transition = 'all 0.2s ease';

      markerDiv.style.left = `${index * 5}px`;
      markerDiv.style.zIndex = events.length - index;

      markerDiv.style.border = '1px solid #000';
      markerDiv.style.opacity = '0.8';

      if (events.length > 1 && index === 0) {
        markerDiv.style.border = '2px solid #0075FF';
        markerDiv.style.opacity = '1';
      }

      const img = document.createElement('img');
      if (!event.icon) {
        img.src = question;
        img.style.backgroundColor = '#f3de1dff';
      } else {
        img.src = event.icon;
      }
      img.alt = event.name;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';

      markerDiv.appendChild(img);
      container.appendChild(markerDiv);
    });

    container.onclick = (e) => {
      if (events.length > 1) {
        setStackedEvents(events);
        setCurrentStackKey(stackKey);

        const rect = e.currentTarget.getBoundingClientRect();
        setPopoverPosition({
          top: rect.bottom,
          left: rect.left + rect.width / 2,
        });

        setTimeout(() => setPopoverAnchor(anchorRef.current), 0);

        e.stopPropagation();
      } else if (!isDraggable) {
        const driveId = events[0].drive_id || events[0].driveId || null;
        handleClickMarker(events[0], driveId);
      }
    };

    // Add badge for stacked markers
    if (events.length > 1) {
      const badge = document.createElement('div');
      Object.assign(badge.style, {
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        backgroundColor: isDraggable ? '#FF6B00' : '#00B09B',
        color: 'white',
        borderRadius: '50%',
        width: '18px',
        height: '18px',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid white',
        zIndex: '1000',
      });
      badge.textContent = events.length;
      container.appendChild(badge);
    }

    // Add edit indicator if draggable
    if (isDraggable) {
      const editIndicator = document.createElement('div');
      Object.assign(editIndicator.style, {
        position: 'absolute',
        bottom: '-10px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#FF6B00',
        color: 'white',
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '10px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        border: '1px solid white',
        zIndex: '1000',
      });
      editIndicator.textContent = events.length > 1 ? 'SELECT' : 'DRAG';
      container.appendChild(editIndicator);
    }

    return container;
  };

  const createSingleDraggableMarker = (event, stackKey) => {
    const container = document.createElement('div');
    container.style.position = 'relative';
    container.style.cursor = 'move';

    const markerDiv = document.createElement('div');
    markerDiv.style.width = '40px';
    markerDiv.style.height = '40px';
    markerDiv.style.borderRadius = '50%';
    markerDiv.style.overflow = 'hidden';
    markerDiv.style.border = '3px solid #FF6B00';
    markerDiv.style.boxShadow = '0 2px 8px rgba(255, 107, 0, 0.4)';

    const img = document.createElement('img');
    if (!event.icon) {
      img.src = question;
      img.style.backgroundColor = '#f3de1dff';
    } else {
      img.src = event.icon;
    }
    img.alt = event.name;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';

    markerDiv.appendChild(img);
    container.appendChild(markerDiv);

    const editIndicator = document.createElement('div');
    editIndicator.style.position = 'absolute';
    editIndicator.style.bottom = '-12px';
    editIndicator.style.left = '50%';
    editIndicator.style.transform = 'translateX(-50%)';
    editIndicator.style.backgroundColor = '#FF6B00';
    editIndicator.style.color = 'white';
    editIndicator.style.borderRadius = '4px';
    editIndicator.style.padding = '2px 8px';
    editIndicator.style.fontSize = '11px';
    editIndicator.style.fontWeight = 'bold';
    editIndicator.style.whiteSpace = 'nowrap';
    editIndicator.style.border = '2px solid white';
    editIndicator.style.zIndex = '1000';
    editIndicator.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
    editIndicator.textContent = 'DRAGGING';
    container.appendChild(editIndicator);

    return container;
  };

  const handleEventSelection = (selectedEvent, eventIndex) => {
    const reorderedEvents = [selectedEvent, ...stackedEvents.filter((_, i) => i !== eventIndex)];
    setStackedEvents(reorderedEvents);

    const grouped = groupMarkersByPosition(markers);
    const stackEvents = grouped[currentStackKey];

    if (stackEvents) {
      const updatedGrouped = { ...grouped };
      updatedGrouped[currentStackKey] = reorderedEvents;
      setGroupedMarkers(updatedGrouped);

      setPopoverAnchor(null);

      const driveId = selectedEvent.drive_id || selectedEvent.driveId || null;
      handleClickMarker(selectedEvent, driveId);
    }
  };

  const handleSelectMarkerForDrag = (event) => {
    setSelectedForDrag(event);
    setPopoverAnchor(null);

    setSnackbar({
      open: true,
      message: `Selected "${event.name}" - Now drag it to a new location`,
      severity: 'info',
    });
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setPopoverPosition(null);
    if (!editMode) {
      setStackedEvents([]);
      setCurrentStackKey('');
    }
  };

  const clearMarkers = () => {
    try {
      Object.values(markerElements).forEach((markerElement) => {
        if (markerElement) {
          if (typeof markerElement.setMap === 'function') {
            markerElement.setMap(null);
          } else if (markerElement.map) {
            markerElement.map = null;
          }
        }
      });
    } catch (e) {
      // Ignore errors during marker cleanup
    }
    setMarkerElements({});
  };

  const renderMarkers = (grouped, draggable = false) => {
    clearMarkers();
    const newMarkerElements = {};

    Object.entries(grouped).forEach(([stackKey, events]) => {
      const position = {
        lat: events[0].lat,
        lng: events[0].lng,
      };

      // If a marker is selected for dragging and it's in this stack
      const selectedEventInStack =
        selectedForDrag && events.find((e) => e.id === selectedForDrag.id);

      if (draggable && selectedEventInStack) {
        // Render the selected marker as draggable
        const draggableContent = createSingleDraggableMarker(selectedForDrag, stackKey);

        const draggableMarker = new window.google.maps.marker.AdvancedMarkerElement({
          position,
          map: mapRef.current,
          title: selectedForDrag.name,
          content: draggableContent,
          gmpDraggable: true,
        });

        draggableMarker.addListener('dragend', (dragEvent) => {
          const newPosition = {
            lat: dragEvent.latLng.lat(),
            lng: dragEvent.latLng.lng(),
          };

          if (onMarkerPositionChange) {
            onMarkerPositionChange(selectedForDrag.id, newPosition);
          }

          setSnackbar({
            open: true,
            message: `"${selectedForDrag.name}" moved to new location. Click "Save Changes" to confirm.`,
            severity: 'success',
          });

          // Reset selection after drag
          setSelectedForDrag(null);
        });

        newMarkerElements[`${stackKey}_draggable`] = draggableMarker;

        // Render the remaining markers in the stack (if any)
        const remainingEvents = events.filter((e) => e.id !== selectedForDrag.id);
        if (remainingEvents.length > 0) {
          const stackedContent = createStackedMarkerContent(remainingEvents, stackKey, draggable);

          const stackMarker = new window.google.maps.marker.AdvancedMarkerElement({
            position,
            map: mapRef.current,
            title:
              remainingEvents.length > 1
                ? `${remainingEvents.length} signs at this location`
                : remainingEvents[0].name,
            content: stackedContent,
            gmpDraggable: false,
          });

          newMarkerElements[`${stackKey}_stack`] = stackMarker;
        }
      } else {
        // Normal rendering
        const stackedMarkerContent = createStackedMarkerContent(events, stackKey, draggable);

        const markerElement = new window.google.maps.marker.AdvancedMarkerElement({
          position,
          map: mapRef.current,
          title: events.length > 1 ? `${events.length} signs at this location` : events[0].name,
          content: stackedMarkerContent,
          gmpDraggable: draggable && events.length === 1,
        });

        // Only single markers can be dragged directly
        if (draggable && events.length === 1) {
          markerElement.addListener('dragend', (dragEvent) => {
            const newPosition = {
              lat: dragEvent.latLng.lat(),
              lng: dragEvent.latLng.lng(),
            };

            if (onMarkerPositionChange) {
              onMarkerPositionChange(events[0].id, newPosition);
            }

            setSnackbar({
              open: true,
              message: `"${events[0].name}" moved to new location. Click "Save Changes" to confirm.`,
              severity: 'success',
            });
          });
        }

        newMarkerElements[stackKey] = markerElement;
      }
    });

    setMarkerElements(newMarkerElements);
  };

  const createPin = (iconClass, bgColor, borderColor) => {
    const iconDiv = document.createElement('div');
    iconDiv.innerHTML = `<i class="${iconClass}"></i>`;
    iconDiv.style.fontSize = '22px';

    return new window.google.maps.marker.PinElement({
      glyph: iconDiv,
      glyphColor: '#FFF',
      background: bgColor,
      borderColor: borderColor,
      scale: 1.3,
    });
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBrYTn6FUEGr_84F1UPtsYa7l_sp4bWWMo&v=weekly&libraries=marker`;
    script.async = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      setMapLoaded(true);
    };

    script.onerror = () => {};

    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (!mapLoaded) return;

    const mapDiv = document.getElementById('map');
    if (!mapDiv) return;

    mapRef.current = new window.google.maps.Map(mapDiv, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapId: '99b311845891871b',
    });

    mapRef.current.addListener('zoom_changed', () => {
      if (mapRef.current.getZoom() > (mapRef.current.__previousZoom || 0)) {
        setShowZoomToSelected(false);
      }
      mapRef.current.__previousZoom = mapRef.current.getZoom();
    });

    return () => {
      // Cleanup map and associated elements
      try {
        clearMarkers();
        if (mapRef.current && mapRef.current.__drivePathElements) {
          const { borderPolyline, polyline, startMarker, endMarker } =
            mapRef.current.__drivePathElements;
          [borderPolyline, polyline, startMarker, endMarker].forEach((el) => {
            if (el) {
              if (typeof el.setMap === 'function') {
                el.setMap(null);
              } else {
                el.map = null;
              }
            }
          });
          mapRef.current.__drivePathElements = null;
        }
        if (mapRef.current && mapRef.current.__addSignMarker) {
          const marker = mapRef.current.__addSignMarker;
          if (typeof marker.setMap === 'function') {
            marker.setMap(null);
          } else {
            marker.map = null;
          }
          mapRef.current.__addSignMarker = null;
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapLoaded) return;

    if (isAddSign) {
      const prev = mapRef.current.__addSignMarker;
      if (prev) {
        if (typeof prev.setMap === 'function') {
          prev.setMap(null);
        } else {
          prev.map = null;
        }
        mapRef.current.__addSignMarker = null;
      }

      if (
        nearestGps &&
        typeof nearestGps.latitude === 'number' &&
        typeof nearestGps.longitude === 'number'
      ) {
        const pos = { lat: nearestGps.latitude, lng: nearestGps.longitude };
        const addPin = createPin('fa fa-plus-circle', '#DC1709', '#DC1709');

        const newMarker = new window.google.maps.marker.AdvancedMarkerElement({
          position: pos,
          map: mapRef.current,
          title: 'Add Sign Here',
          content: addPin.element,
          gmpDraggable: true,
        });

        newMarker.addListener('dragend', (dragEvent) => {
          const newPos = { lat: dragEvent.latLng.lat(), lng: dragEvent.latLng.lng() };
          setAddSignLocation(newPos);
          if (mapRef.current) {
            mapRef.current.setCenter(newPos);
          }
        });

        mapRef.current.__addSignMarker = newMarker;
        setAddSignLocation(pos);

        if (mapRef.current) {
          mapRef.current.setCenter(pos);
          try {
            const currentZoom = mapRef.current.getZoom?.() ?? 14;
            if (currentZoom < 15) mapRef.current.setZoom(15);
          } catch (e) {
            /* ignore */
          }
        }
      }
    } else {
      mapRef.current.setOptions({ draggableCursor: 'auto' });

      const prev = mapRef.current.__addSignMarker;
      if (prev) {
        if (typeof prev.setMap === 'function') {
          prev.setMap(null);
        } else {
          prev.map = null;
        }
        mapRef.current.__addSignMarker = null;
      }
    }
  }, [isAddSign, mapLoaded, nearestGps]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    if (gpsDataLoading) return;

    // remove previous path/markers before drawing a new one
    if (mapRef.current.__drivePathElements) {
      const { borderPolyline, polyline, startMarker, endMarker } =
        mapRef.current.__drivePathElements;
      [borderPolyline, polyline, startMarker, endMarker].forEach((el) => {
        if (el) {
          if (typeof el.setMap === 'function') {
            el.setMap(null);
          } else {
            el.map = null;
          }
        }
      });
      mapRef.current.__drivePathElements = null;
    }

    if (!gpsData || gpsData.length === 0) {
      setSnackbar({
        open: true,
        message: 'GPS Coordinates unavailable. Contact Rieker Admin for support.',
        severity: 'warning',
      });
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    const path = gpsData.map((point) => {
      const latLng = new window.google.maps.LatLng(point.latitude, point.longitude);
      bounds.extend(latLng);
      return {
        lat: point.latitude,
        lng: point.longitude,
      };
    });

    const center = {
      lat: (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2,
      lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2,
    };

    mapRef.current.setCenter(center);

    const PADDING = 12;
    mapRef.current.fitBounds(bounds, PADDING);

    const borderPolyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#004DA8',
      strokeOpacity: 1.0,
      strokeWeight: 8,
      zIndex: 1,
    });

    const polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#0075FF',
      strokeOpacity: 0.9,
      strokeWeight: 3,
      zIndex: 2,
    });

    borderPolyline.setMap(mapRef.current);
    polyline.setMap(mapRef.current);

    const startPin = createPin('fa fa-circle-play', '#24BD05', '#24BD05');
    const endPin = createPin('fa fa-circle-stop', '#DC1709', '#DC1709');

    const startMarker = new window.google.maps.marker.AdvancedMarkerElement({
      position: path[0],
      map: mapRef.current,
      title: 'Start',
      content: startPin.element,
    });

    const endMarker = new window.google.maps.marker.AdvancedMarkerElement({
      position: path[path.length - 1],
      map: mapRef.current,
      title: 'End',
      content: endPin.element,
    });

    // store references so we can clear them later
    mapRef.current.__drivePathElements = {
      borderPolyline,
      polyline,
      startMarker,
      endMarker,
    };

    setSnackbar((prev) => ({ ...prev, open: false }));

    return () => {
      // cleanup when component unmounts or gpsData changes again
      if (mapRef.current && mapRef.current.__drivePathElements) {
        const { borderPolyline, polyline, startMarker, endMarker } =
          mapRef.current.__drivePathElements;
        [borderPolyline, polyline, startMarker, endMarker].forEach((el) => {
          if (el) {
            if (typeof el.setMap === 'function') {
              el.setMap(null);
            } else {
              el.map = null;
            }
          }
        });
        mapRef.current.__drivePathElements = null;
      }
    };
  }, [gpsData, gpsDataLoading, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    if (!markers || markers.length === 0 || !showSigns) {
      // clear any existing markers when supplied list is empty or showSigns is false
      clearMarkers();
      setGroupedMarkers({});
      return;
    }

    const grouped = groupMarkersByPosition(markers);

    if (currentStackKey && stackedEvents.length > 0 && grouped[currentStackKey]) {
      grouped[currentStackKey] = stackedEvents;
    }

    setGroupedMarkers(grouped);
    renderMarkers(grouped, editMode);
  }, [markers, mapLoaded, stackedEvents, currentStackKey, editMode, selectedForDrag, showSigns]);

  useEffect(() => {
    if (
      !mapLoaded ||
      !mapRef.current ||
      !selectedMarker ||
      Object.keys(selectedMarker).length === 0
    )
      return;

    const position = { lat: selectedMarker.lat, lng: selectedMarker.lng };
    mapRef.current.setCenter(position);
    mapRef.current.setZoom(18);

    const stackKey = `${selectedMarker.lat}_${selectedMarker.lng}`;
    const stackEvents = groupedMarkers[stackKey];
    if (stackEvents && stackEvents.length > 1) {
      const reorderedEvents = [
        selectedMarker,
        ...stackEvents.filter((event) => event.id !== selectedMarker.id),
      ];
      setStackedEvents(reorderedEvents);
      setCurrentStackKey(stackKey);

      const updatedGrouped = { ...groupedMarkers };
      updatedGrouped[stackKey] = reorderedEvents;
      setGroupedMarkers(updatedGrouped);
    } else {
      setStackedEvents([]);
      setCurrentStackKey('');
    }
  }, [selectedMarker, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    if (showFullPath) {
      const bounds = new window.google.maps.LatLngBounds();

      gpsData.forEach((point) => {
        const latLng = new window.google.maps.LatLng(point.latitude, point.longitude);
        bounds.extend(latLng);
      });

      const PADDING = 12;
      mapRef.current.fitBounds(bounds, PADDING);
      setShowFullPath(false);
    }
  }, [showFullPath, mapLoaded]);

  // Reset selected marker when exiting edit mode
  useEffect(() => {
    if (!editMode) {
      setSelectedForDrag(null);
    }
  }, [editMode]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    if (zoomToSelected && selectedMarker && Object.keys(selectedMarker).length !== 0) {
      const position = { lat: selectedMarker.lat, lng: selectedMarker.lng };
      mapRef.current.setCenter(position);
      mapRef.current.setZoom(18);
      setZoomToSelected(false);
    }
  }, [zoomToSelected, selectedMarker, mapLoaded]);

  return (
    <>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {showEditToggle && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 60,
              zIndex: 1000,
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: 2,
              padding: '4px 8px',
            }}
          >
            <Tooltip
              title={
                editMode
                  ? 'Exit edit mode to interact normally'
                  : 'Enable edit mode to re-position signs on the map'
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={editMode}
                    onChange={(e) => setEditMode(e.target.checked)}
                    size="small"
                  />
                }
                label={<span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Edit Mode</span>}
              />
            </Tooltip>
          </Box>
        )}

        <Box
          id="map"
          sx={{
            width: '100%',
            height: '70vh',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        />

        <div
          ref={anchorRef}
          style={{
            position: 'fixed',
            top: popoverPosition?.top || 0,
            left: popoverPosition?.left || 0,
            width: 1,
            height: 1,
            pointerEvents: 'none',
          }}
        />
      </Box>

      <Popover
        open={Boolean(popoverAnchor) && Boolean(popoverPosition)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{ paper: { sx: { maxWidth: 250, maxHeight: 300 } } }}
        anchorReference="anchorEl"
      >
        <Paper elevation={1}>
          {editMode && (
            <Box sx={{ p: 1, backgroundColor: '#FFF3E0', borderBottom: '1px solid #FFE0B2' }}>
              <span style={{ fontSize: '0.75rem', color: '#E65100', fontWeight: 600 }}>
                🎯 Click a marker to select it for dragging
              </span>
            </Box>
          )}
          <List dense>
            {stackedEvents.map((event, index) => (
              <ListItem
                key={`${event.id}-${index}`}
                button
                onClick={() =>
                  editMode ? handleSelectMarkerForDrag(event) : handleEventSelection(event, index)
                }
                sx={{
                  borderLeft: index === 0 ? '4px solid #0075FF' : 'none',
                  backgroundColor: index === 0 ? 'rgba(0, 117, 255, 0.05)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 117, 255, 0.1)',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={event.icon || question}
                    alt={event.name}
                    sx={{
                      width: 32,
                      height: 32,
                      border: index === 0 ? '2px solid #0075FF' : '1px solid #ddd',
                    }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={event.name}
                  secondary={
                    editMode
                      ? 'Click to select for dragging'
                      : index === 0
                        ? 'Currently selected'
                        : 'Click to view sign'
                  }
                  slotProps={{
                    primary: { fontSize: '0.875rem', fontWeight: index === 0 ? 600 : 400 },
                    secondary: { fontSize: '0.75rem' },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>
    </>
  );
};

MapComponent.defaultProps = {
  markers: [],
  handleClickMarker: () => {},
  setSnackbar: () => {},
  selectedMarker: {},
  showFullPath: false,
  setShowFullPath: () => {},
  onMarkerPositionChange: null,
  setShowZoomToSelected: () => {},
  zoomToSelected: false,
  setZoomToSelected: () => {},
  isAddSign: false,
  setAddSignLocation: () => {},
  allowEditMode: true,
  showSigns: true,
};

export default MapComponent;

MapComponent.propTypes = {
  markers: PropTypes.array.isRequired,
  handleClickMarker: PropTypes.func.isRequired,
  setSnackbar: PropTypes.func.isRequired,
  selectedMarker: PropTypes.object.isRequired,
  showFullPath: PropTypes.bool.isRequired,
  setShowFullPath: PropTypes.func.isRequired,
  onMarkerPositionChange: PropTypes.func,
  setShowZoomToSelected: PropTypes.func.isRequired,
  zoomToSelected: PropTypes.bool.isRequired,
  setZoomToSelected: PropTypes.func.isRequired,
  isAddSign: PropTypes.bool.isRequired,
  setAddSignLocation: PropTypes.func.isRequired,
  allowEditMode: PropTypes.bool,
  showSigns: PropTypes.bool,
};
